import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { 
  Search01Icon, 
  FilterHorizontalIcon,
  ArrowLeft01Icon,
  ViewIcon
} from '@hugeicons/core-free-icons';

const AdminOrders = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  // Mock data
  const orders = Array.from({ length: 15 }, (_, i) => ({
    id: `ord_${i + 5000}`,
    user: `User ${i % 5 + 1}`,
    amount: `$${(Math.random() * 500).toFixed(2)}`,
    status: ['completed', 'pending', 'processing', 'cancelled'][i % 4],
    date: new Date(Date.now() - Math.random() * 1000000000).toISOString(),
    items: i % 3 + 1
  }));

  const filteredOrders = orders.filter(order => 
    order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.user.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-orange-100 text-orange-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <button 
            onClick={() => navigate('/admin')}
            className="mr-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
          >
            <HugeiconsIcon icon={ArrowLeft01Icon} size={24} className="text-charcoal dark:text-white" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-charcoal dark:text-white">Order Management</h1>
            <p className="text-muted-gray">Track and manage customer orders</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-dark-surface p-4 rounded-2xl border border-light-gray dark:border-dark-border shadow-sm mb-6 flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <HugeiconsIcon icon={Search01Icon} size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-gray" />
          <input
            type="text"
            placeholder="Search orders by ID or user..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-off-white/50 dark:bg-gray-800/50 border border-light-gray dark:border-gray-700 rounded-xl focus:ring-soft-green focus:border-soft-green outline-none text-charcoal dark:text-white"
          />
        </div>
        <button className="flex items-center justify-center px-4 py-2 border border-light-gray dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 text-charcoal dark:text-white">
          <HugeiconsIcon icon={FilterHorizontalIcon} size={20} className="mr-2" />
          Filter Status
        </button>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-dark-surface rounded-2xl border border-light-gray dark:border-dark-border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b border-light-gray dark:border-gray-700">
              <tr>
                <th className="px-6 py-4 text-xs font-bold text-muted-gray uppercase tracking-wider">Order ID</th>
                <th className="px-6 py-4 text-xs font-bold text-muted-gray uppercase tracking-wider">Customer</th>
                <th className="px-6 py-4 text-xs font-bold text-muted-gray uppercase tracking-wider">Amount</th>
                <th className="px-6 py-4 text-xs font-bold text-muted-gray uppercase tracking-wider">Items</th>
                <th className="px-6 py-4 text-xs font-bold text-muted-gray uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-xs font-bold text-muted-gray uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-xs font-bold text-muted-gray uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-light-gray dark:divide-gray-700">
              {filteredOrders.map((order) => (
                <tr 
                  key={order.id} 
                  className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/admin/orders/${order.id}`)}
                >
                  <td className="px-6 py-4 whitespace-nowrap font-mono text-sm font-bold text-charcoal dark:text-white">
                    #{order.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-xs font-bold text-purple-700">U</span>
                      </div>
                      <span className="text-sm text-charcoal dark:text-white">{order.user}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-charcoal dark:text-white">
                    {order.amount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-gray">
                    {order.items} items
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-bold rounded-full uppercase ${getStatusColor(order.status)}`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-gray">
                    {new Date(order.date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button className="text-gray-400 hover:text-soft-green">
                      <HugeiconsIcon icon={ViewIcon} size={20} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminOrders;
